"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { type RefObject, useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Button } from "@repo/ui/button";
import { Icon } from "@repo/ui/icon";
import { cn } from "@repo/ui/utils";
import { ActionLink } from "@/components/common/actions/actions.component";
import { Link } from "@/i18n/navigation";
import type { SubSiteData } from "@/lib/sub-sites/get-sub-site";
import { getI18nString } from "@/lib/utils";
import { activeSectionKey, isOverHero } from "./sub-site-header.utils";
interface SubSiteHeaderProps {
  site: SubSiteData;
  /** The page opens with a hero. */
  hasHero?: boolean;
}
function useOverHero(initial) {
  const $ = _c(2);
  const [overHero, setOverHero] = useState(initial);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const measure = () => {
        const hero = document.querySelector("[data-hero]");
        if (!hero) {
          return;
        }
        const content = document.querySelector("[data-parallax-content]");
        const navbarHeight = parseFloat(getComputedStyle(hero).getPropertyValue("--navbar-height")) || 0;
        const edge = content ? content.getBoundingClientRect().top : hero.getBoundingClientRect().bottom;
        setOverHero(isOverHero(edge, navbarHeight));
      };
      measure();
      window.addEventListener("scroll", measure, {
        passive: true
      });
      window.addEventListener("resize", measure);
      window.addEventListener("load", measure);
      return () => {
        window.removeEventListener("scroll", measure);
        window.removeEventListener("resize", measure);
        window.removeEventListener("load", measure);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return overHero;
}
function useActiveSection(blockKeys, headerRef) {
  const $ = _c(6);
  const [active, setActive] = useState(null);
  let t0;
  if ($[0] !== blockKeys) {
    t0 = blockKeys.join(",");
    $[0] = blockKeys;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const signature = t0;
  let t1;
  let t2;
  if ($[2] !== headerRef || $[3] !== signature) {
    t1 = () => {
      const keys = signature.split(",").filter(Boolean);
      if (keys.length === 0) {
        return;
      }
      const measure = () => {
        const sections = keys.flatMap(_temp);
        const atPageBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
        const line = (headerRef.current?.offsetHeight ?? 0) + 1;
        setActive(activeSectionKey(sections, line, atPageBottom));
      };
      measure();
      window.addEventListener("scroll", measure, {
        passive: true
      });
      window.addEventListener("resize", measure);
      return () => {
        window.removeEventListener("scroll", measure);
        window.removeEventListener("resize", measure);
      };
    };
    t2 = [signature, headerRef];
    $[2] = headerRef;
    $[3] = signature;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return active;
}
function _temp(key) {
  const element = document.getElementById(key);
  return element ? [{
    key,
    top: element.getBoundingClientRect().top
  }] : [];
}
function navItemHref(item: NonNullable<SubSiteData["navigation"]>["items"] extends Array<infer T> | null ? T : never, homeSlug: string | null | undefined): string {
  if (item.kind === "page" && item.page?.slug) return item.page.slug;
  const base = homeSlug ?? "";
  return `${base}#${item.blockKey ?? ""}`;
}
function VaUtilityStrip(t0) {
  const $ = _c(4);
  const {
    showVaLink
  } = t0;
  let t1;
  if ($[0] !== showVaLink) {
    t1 = showVaLink && <Link href="/" className="typography-va-strip-link flex items-center gap-2 rounded-sm hover:opacity-80 focus-visible:outline-2 focus-visible:outline-current" data-testid="va-strip-wordmark">{"V&A Waterfront"}<Icon name="arrow-right" className="size-4" aria-hidden="true" /></Link>;
    $[0] = showVaLink;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="dark flex h-10 items-center justify-end gap-3 bg-surface-base-primary px-4 text-text-primary desktop-sm:h-[54px] desktop-sm:px-12" data-testid="va-utility-strip">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function SubSiteNavbar(t0) {
  const $ = _c(73);
  const {
    site,
    overHero,
    headerRef
  } = t0;
  const [open, setOpen] = useState(false);
  const locale = useLocale();
  const t = useTranslations("navigation");
  const logo = site.branding?.logo;
  const onDark = overHero || open;
  const logoSrc = onDark ? logo?.dark ?? logo?.light : logo?.light ?? logo?.dark;
  const homeSlug = site.routing?.homePage?.slug ?? site.routing?.basePath;
  let t1;
  if ($[0] !== site.navigation?.items) {
    t1 = site.navigation?.items ?? [];
    $[0] = site.navigation?.items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const items = t1;
  let t2;
  if ($[2] !== items) {
    t2 = items.flatMap(_temp2);
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const activeSection = useActiveSection(t2, headerRef);
  const cta = site.navigation?.cta;
  const t3 = cta?.text;
  let t4;
  if ($[4] !== locale || $[5] !== t3) {
    t4 = getI18nString(t3, "", locale);
    $[4] = locale;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const ctaLabel = t4;
  const t5 = open ? "dark bg-surface-base-primary text-text-primary" : overHero ? "dark bg-transparent text-text-primary" : "bg-surface-base-primary text-text-primary shadow-sm";
  let t6;
  if ($[7] !== t5) {
    t6 = cn("transition-colors duration-300 motion-reduce:transition-none", t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = open || undefined;
  const t8 = homeSlug ?? "/";
  const t9 = site.name ?? "";
  let t10;
  if ($[9] !== t || $[10] !== t9) {
    t10 = t("subSiteHome", {
      site: t9
    });
    $[9] = t;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== logo?.name || $[13] !== logoSrc || $[14] !== site.name) {
    t11 = logoSrc ? <Image src={logoSrc} alt={logo?.name ?? site.name ?? ""} width={191} height={62} className="h-10 w-auto desktop-sm:h-[62px]" /> : <span className="typography-desktop-heading-overline">{site.name}</span>;
    $[12] = logo?.name;
    $[13] = logoSrc;
    $[14] = site.name;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== t8) {
    t12 = <Link href={t8} className="flex shrink-0 items-center" aria-label={t10}>{t11}</Link>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t8;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = site.name ?? "";
  let t14;
  if ($[20] !== t || $[21] !== t13) {
    t14 = t("subSiteNavigation", {
      site: t13
    });
    $[20] = t;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== activeSection || $[24] !== homeSlug || $[25] !== items) {
    let t16;
    if ($[27] !== activeSection || $[28] !== homeSlug) {
      t16 = item_0 => {
        const isActive = Boolean(item_0.blockKey && item_0.blockKey === activeSection);
        return <Link key={item_0._key} href={navItemHref(item_0, homeSlug)} aria-current={isActive ? "true" : undefined} className={cn("typography-component-xs-medium whitespace-nowrap border-b transition-colors hover:opacity-80", isActive ? "border-current" : "border-transparent")}>{item_0.label}</Link>;
      };
      $[27] = activeSection;
      $[28] = homeSlug;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    t15 = items.map(t16);
    $[23] = activeSection;
    $[24] = homeSlug;
    $[25] = items;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[30] !== t14 || $[31] !== t15) {
    t16 = <nav aria-label={t14} className="hidden flex-1 items-center justify-center gap-8 desktop-sm:flex">{t15}</nav>;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== cta || $[34] !== ctaLabel || $[35] !== overHero) {
    t17 = cta?.to && <Button asChild={true} variant={overHero ? "blurred" : "blue-filled"} size="sm" className="hidden desktop-sm:inline-flex"><ActionLink actionTo={cta.to}>{ctaLabel}</ActionLink></Button>;
    $[33] = cta;
    $[34] = ctaLabel;
    $[35] = overHero;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== items.length || $[38] !== open || $[39] !== t) {
    t18 = items.length > 0 && <button type="button" onClick={() => setOpen(_temp3)} aria-label={open ? t("closeSectionMenu") : t("openSectionMenu")} aria-expanded={open} aria-controls="sub-site-menu" className="flex size-10 items-center justify-center rounded-sm desktop-sm:hidden"><Icon name={open ? "close" : "menu"} className="size-6" /></button>;
    $[37] = items.length;
    $[38] = open;
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t17 || $[42] !== t18) {
    t19 = <div className="flex items-center gap-3">{t17}{t18}</div>;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t12 || $[45] !== t16 || $[46] !== t19) {
    t20 = <div className="flex h-16 items-center justify-between gap-6 px-4 desktop-sm:h-[118px] desktop-sm:px-12">{t12}{t16}{t19}</div>;
    $[44] = t12;
    $[45] = t16;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  const t21 = !open;
  const t22 = open ? "grid-rows-[1fr]" : "grid-rows-[0fr]";
  let t23;
  if ($[48] !== t22) {
    t23 = cn("grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none desktop-sm:hidden", t22);
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== activeSection || $[51] !== homeSlug || $[52] !== items) {
    let t25;
    if ($[54] !== activeSection || $[55] !== homeSlug) {
      t25 = item_1 => {
        const isActive_0 = Boolean(item_1.blockKey && item_1.blockKey === activeSection);
        return <Link key={item_1._key} href={navItemHref(item_1, homeSlug)} onClick={() => setOpen(false)} aria-current={isActive_0 ? "true" : undefined} className={cn("typography-component-md-medium py-2", isActive_0 && "text-brand-secondary")}>{item_1.label}</Link>;
      };
      $[54] = activeSection;
      $[55] = homeSlug;
      $[56] = t25;
    } else {
      t25 = $[56];
    }
    t24 = items.map(t25);
    $[50] = activeSection;
    $[51] = homeSlug;
    $[52] = items;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[57] !== cta || $[58] !== ctaLabel) {
    t25 = cta?.to && <Button asChild={true} variant="white-filled" size="sm" className="mt-2"><ActionLink actionTo={cta.to}>{ctaLabel}</ActionLink></Button>;
    $[57] = cta;
    $[58] = ctaLabel;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t24 || $[61] !== t25) {
    t26 = <div className="overflow-hidden"><div className="grid gap-1 border-t border-border-subtle px-4 py-4">{t24}{t25}</div></div>;
    $[60] = t24;
    $[61] = t25;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== t21 || $[64] !== t23 || $[65] !== t26) {
    t27 = <div id="sub-site-menu" inert={t21} className={t23}>{t26}</div>;
    $[63] = t21;
    $[64] = t23;
    $[65] = t26;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== overHero || $[68] !== t20 || $[69] !== t27 || $[70] !== t6 || $[71] !== t7) {
    t28 = <div className={t6} data-testid="sub-site-navbar" data-over-hero={overHero} data-menu-open={t7}>{t20}{t27}</div>;
    $[67] = overHero;
    $[68] = t20;
    $[69] = t27;
    $[70] = t6;
    $[71] = t7;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  return t28;
}
function _temp3(value) {
  return !value;
}
function _temp2(item) {
  return item.kind !== "page" && item.blockKey ? [item.blockKey] : [];
}
export function SubSiteHeader(t0) {
  const $ = _c(16);
  const {
    site,
    hasHero: t1
  } = t0;
  const hasHero = t1 === undefined ? false : t1;
  const overHero = useOverHero(hasHero);
  const headerRef = useRef(null);
  const showVaLink = site.navigation?.showVaLink !== false;
  const navBlur = Boolean(site.navigation?.navBlur);
  const t2 = navBlur && overHero && "backdrop-blur-md";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("fixed inset-x-0 top-0 z-40", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = navBlur || undefined;
  let t5;
  if ($[2] !== showVaLink) {
    t5 = <VaUtilityStrip showVaLink={showVaLink} />;
    $[2] = showVaLink;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== overHero || $[5] !== site) {
    t6 = <SubSiteNavbar site={site} overHero={overHero} headerRef={headerRef} />;
    $[4] = overHero;
    $[5] = site;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== site.key || $[8] !== t3 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = <header ref={headerRef} className={t3} data-nav-blur={t4} data-testid="sub-site-header" data-sub-site={site.key}>{t5}{t6}</header>;
    $[7] = site.key;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="h-[var(--navbar-height)]" data-testid="sub-site-header-spacer" aria-hidden="true" />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7) {
    t9 = <>{t7}{t8}</>;
    $[14] = t7;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}